// pages/ToolsPage.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Stack,
  Divider,
  Collapse,
  HStack,
  IconButton,
  Badge,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";

const tools = [
  {
    title: "IVR Checker - Works",
    description: "Look up the status of submitted IVRs and view form details.",
    route: "/tools/ivr-checker",
  },

  // ✅ NEW: Support impersonation tool
  {
    title: "View As Customer (Read-only)",
    description:
      "Open the customer portal as the selected user. Actions are disabled; this is for support visibility only.",
    route: "/tools/view-as-customer",
    badge: "NEW",
  },

  {
    title: "Order Updates (WIP)",
    description: "Manage medical or aesthetic product orders and shipping status.",
    route: "/tools/order-updates",
  },
  {
    title: "Update User Info",
    description: "View and manage user info.",
    route: "/tools/users",
  },
  {
    title: "Create Order (Admin)",
    description: "Create orders on behalf of users and assign products.",
    route: "/tools/create-order",
    requiresRole: "admin",
  },
  {
    title: "Payment Reminders (WIP)",
    description: "Send or review outstanding payment reminders to providers.",
    route: "/tools/payment-reminders",
  },
  {
    title: "CRM Tools (WIP)",
    description: "Track leads, communications, and customer touchpoints.",
    route: "/tools/crm",
  },
  {
    title: "BAA/Agreements",
    description: "Check completion status and download signed agreements.",
    route: "/tools/agreements",
  },
  {
    title: "Facilities Management",
    description: "Manage facility standing status and view facility information.",
    route: "/tools/facilities-management",
  },
  {
    title: "Admin Actions (WIP)",
    description: "Assign roles, disable users, and review audit logs.",
    route: "/tools/admin-actions",
    requiresRole: "admin",
  },
];

export default function ToolsPage() {
  const [showFuture, setShowFuture] = useState(false);

  const { workingTools, futureTools } = useMemo(() => {
    const working = tools.filter((t) => !t.title.includes("(WIP)"));
    const future = tools.filter((t) => t.title.includes("(WIP)"));
    return { workingTools: working, futureTools: future };
  }, []);

  const renderToolCards = (toolList) => (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
      {toolList.map((tool, index) => (
        <Card key={index} variant="outline" borderRadius="xl" shadow="md">
          <CardHeader pb={0}>
            <HStack justify="space-between" align="flex-start">
              <Heading size="md">{tool.title}</Heading>
              {tool.badge && (
                <Badge colorScheme="yellow" variant="subtle" borderRadius="md">
                  {tool.badge}
                </Badge>
              )}
            </HStack>
          </CardHeader>
          <CardBody>
            <Stack spacing={4}>
              <Text>{tool.description}</Text>
              <Button
                as={RouterLink}
                to={tool.route}
                colorScheme="teal"
                variant="solid"
                _hover={{ transform: "scale(1)" }}
                transition="transform 0.12s ease-out"
              >
                Open
              </Button>
            </Stack>
          </CardBody>
        </Card>
      ))}
    </SimpleGrid>
  );

  return (
    <Box p={6}>
      <Heading size="xl" mb={6}>
        Internal Tools
      </Heading>

      {renderToolCards(workingTools)}

      {futureTools.length > 0 && (
        <>
          <Divider my={10} />

          <HStack justify="space-between" align="center" mb={3}>
            <Heading size="md">Future Functionality</Heading>
            <HStack>
              <Text fontSize="sm" color="gray.500">
                {showFuture ? "Hide" : "Show"} ({futureTools.length})
              </Text>
              <IconButton
                aria-label={showFuture ? "Hide future tools" : "Show future tools"}
                icon={showFuture ? <ChevronDownIcon /> : <ChevronRightIcon />}
                onClick={() => setShowFuture((s) => !s)}
                size="sm"
                variant="outline"
                borderRadius="lg"
                _hover={{ transform: "scale(1)" }}
                transition="transform 0.12s ease-out"
              />
            </HStack>
          </HStack>

          <Collapse in={showFuture} animateOpacity>
            {renderToolCards(futureTools)}
          </Collapse>
        </>
      )}
    </Box>
  );
}
